import type { IdeaRole } from './tradeIdeaVocab'

/**
 * The live measurement under a trade idea's stop loss and take profit (PROD-3131): how
 * far each sits from the entry in pips, and for the target the reward-to-risk ratio.
 * Shown while a level is being placed and while a placed one is dragged.
 */

/**
 * A pip is ten points, the rule server-ai resolves for every backtest
 * (MarketMetaResolver: `pip_size = point_size * 10`), so the readout and the run's
 * results count in the same unit. Without a point size, the server's own default
 * (0.00001) applies.
 */
export const DEFAULT_POINT_SIZE = 0.00001

export function pipSizeFor(pointSize: number | null | undefined): number {
  const point = typeof pointSize === 'number' && Number.isFinite(pointSize) && pointSize > 0 ? pointSize : DEFAULT_POINT_SIZE
  return point * 10
}

/** Pips between two prices, unsigned: the geometry lock already settles which side a level sits. */
export function pipsBetween(a: number, b: number, pipSize: number): number {
  return Math.abs(a - b) / pipSize
}

/** One decimal always, so the plate keeps its width as the pointer moves. */
const fmt = (n: number) => n.toFixed(1)

/** The first line of the guide while dragging a placed level. */
export const MOVING_PROMPT: Record<IdeaRole, string> = {
  entry: 'Moving entry',
  sl: 'Moving stop loss',
  tp: 'Moving take profit',
}

export interface ReadoutInput {
  role: IdeaRole
  /** The price under the pointer, or the dragged level's price. */
  price: number | null | undefined
  entry: number | null | undefined
  /** The idea's stop loss, for the target's ratio. */
  stop: number | null | undefined
  pipSize: number
}

/**
 * "Risk 41.5 pips" for a stop loss; "Profit 63.7 pips · 1:2.2 RR" for a take profit, or
 * the pips alone while there is no stop to divide by. Null when there is nothing true
 * to say: the entry itself, or no entry yet to measure from.
 */
export function levelReadout({ role, price, entry, stop, pipSize }: ReadoutInput): string | null {
  if (role === 'entry') return null
  if (price == null || entry == null || !Number.isFinite(price) || !Number.isFinite(entry)) return null
  const pips = pipsBetween(price, entry, pipSize)
  if (role === 'sl') return `Risk ${fmt(pips)} pips`
  const risk = stop != null && Number.isFinite(stop) ? Math.abs(entry - stop) : 0
  if (!risk) return `Profit ${fmt(pips)} pips`
  return `Profit ${fmt(pips)} pips · 1:${fmt(Math.abs(price - entry) / risk)} RR`
}

export interface DragReadoutInput {
  role: IdeaRole
  /** The dragged level's price. */
  price: number | null | undefined
  entry: number | null | undefined
  stop: number | null | undefined
  /** The idea's first take profit, for the ratio. */
  tp: number | null | undefined
  pipSize: number
}

/**
 * The readout of a placed level being dragged, as the prototype words it (v6 sgDragReadout): the
 * placing wording, with the ratio wherever both sides of it exist.
 *   take profit → "Profit 30.9 pips · 1:2.2 RR"
 *   stop loss   → "Risk 33.5 pips · 1:2.2 RR" (against the first take profit)
 *   entry       → "Risk 33.5 pips · Profit 30.9 pips · 1:2.2 RR"
 * A level that is not placed drops its part; null when there is nothing to say.
 */
export function dragReadout({ role, price, entry, stop, tp, pipSize }: DragReadoutInput): string | null {
  const ok = (v: number | null | undefined): v is number => v != null && Number.isFinite(v)
  const e = role === 'entry' ? price : entry
  const s = role === 'sl' ? price : stop
  const t = role === 'tp' ? price : tp
  if (!ok(e)) return null
  const risk = ok(s) ? Math.abs(e - s) : 0
  const reward = ok(t) ? Math.abs(t - e) : 0
  const rr = risk && reward ? `1:${fmt(reward / risk)} RR` : null
  const parts: string[] = []
  if (role === 'tp' && ok(t)) parts.push(`Profit ${fmt(pipsBetween(t, e, pipSize))} pips`)
  if (role === 'sl' && ok(s)) parts.push(`Risk ${fmt(pipsBetween(s, e, pipSize))} pips`)
  if (role === 'entry') {
    if (ok(s)) parts.push(`Risk ${fmt(pipsBetween(s, e, pipSize))} pips`)
    if (ok(t)) parts.push(`Profit ${fmt(pipsBetween(t, e, pipSize))} pips`)
  }
  if (!parts.length) return null
  if (rr) parts.push(rr)
  return parts.join(' · ')
}
